import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import {
  ArrowRight, ArrowDown, ArrowLeftRight, Check, CheckCircle,
  Package, Truck, ScanBarcode, Factory, ShieldCheck, Database,
  Cpu, Layers, BarChart3, ClipboardCheck, Warehouse, RefreshCw,
  Users, FileSearch, MessageSquareWarning, Wrench, GraduationCap,
  Rocket, Ruler, Zap, GitBranch, AlertTriangle, UserPlus,
  FileText, Award, Star, LayoutDashboard, Monitor, Tablet,
  Smartphone, Laptop, Eye, Search, ChevronRight
} from 'lucide-react';
import BookDemoDialog from '@/components/BookDemoDialog';

const RED = '#E02D45';
const GREEN = '#34D399';
const BLUE = '#38BDF8';
const AMBER = '#F59E0B';

function Section({ children, className = '', id }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} id={id} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`}>
      {children}
    </section>
  );
}

function TypewriterLine({ text, visible, startDelay = 0, highlightColor = null }) {
  return (
    <div className="overflow-hidden">
      <span className="inline-block">
        {text.split('').map((char, i) => (
          <span
            key={i}
            className={`inline-block typewriter-char ${visible ? 'typewriter-char-visible' : ''}`}
            style={{ transitionDelay: `${startDelay + i * 60}ms`, color: highlightColor || undefined }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════════════ */

const brokenReasons = [
  { icon: FileText, title: 'Paper-Based Records', desc: 'Quality checksheets, store registers, and inspection reports still live on paper. Searchable traceability is impossible.' },
  { icon: Layers, title: 'Disconnected Software', desc: 'ERP handles procurement, a different tool handles quality, and production runs on Excel. Nothing is linked.' },
  { icon: ScanBarcode, title: 'Barcodes Without Integration', desc: 'Barcode labels exist, but scanning is not connected to quality records, machine events, or supplier data.' },
  { icon: Cpu, title: 'Machine Data in Silos', desc: 'IoT dashboards show OEE and runtime, but machine events are not tied to product genealogy or batch records.' },
  { icon: Warehouse, title: 'No Stores-to-Production Link', desc: 'Material is issued from stores, but there is no digital linkage to the production lot, operator, or inspection result.' },
  { icon: MessageSquareWarning, title: 'Weak Backward Trace', desc: 'When a complaint or recall arrives, teams spend days collecting paper records across departments to reconstruct what happened.' },
];

const traceStages = [
  { label: 'Raw Material Received', detail: 'Linked to supplier, lot number, and certificate', color: BLUE },
  { label: 'Incoming Inspection', detail: 'GRN-linked IQC, sampling plan, and acceptance criteria', color: BLUE },
  { label: 'Digital Stores Entry', detail: 'Bin, location, FIFO, shelf-life, and lot tracking', color: GREEN },
  { label: 'Material Issue', detail: 'Issued to production with lot, quantity, and operator linkage', color: GREEN },
  { label: 'Production Execution', detail: 'Operator, machine, shift, line, and cycle-time linkage', color: RED },
  { label: 'In-Process Inspection', detail: 'Stage-wise checksheets, SPC, and process parameters', color: RED },
  { label: 'Machine & Sensor Events', detail: 'IoT-linked runtime, alarms, OEE, and process events', color: AMBER },
  { label: 'Final Inspection / PDI', detail: 'Final quality sign-off, dimensional checks, and test results', color: RED },
  { label: 'Packing & Dispatch', detail: 'Batch, shipment, customer, and dispatch documentation', color: GREEN },
];

const factoryOSApps = [
  { icon: ClipboardCheck, name: 'Incoming Quality', slug: 'incoming-quality', role: 'Links every received lot to supplier, certificate, and inspection result' },
  { icon: Warehouse, name: 'Digital Stores', slug: 'digital-stores', role: 'Tracks material from bin to issue with FIFO, lot, and shelf-life data' },
  { icon: Factory, name: 'Production Execution', slug: 'production-execution', role: 'Captures operator, machine, shift, and output for every production event' },
  { icon: ShieldCheck, name: 'Quality Management', slug: 'quality-management', role: 'Runs inspection checksheets, SPC, and process quality controls digitally' },
  { icon: RefreshCw, name: 'Change Management', slug: 'change-management', role: 'Records every 4M change event and links it to affected production' },
  { icon: MessageSquareWarning, name: 'Complaints & CAPA', slug: 'complaints-capa', role: 'Links complaints and CAPA actions back to batch, process, and supplier' },
  { icon: FileSearch, name: 'Audit Management', slug: 'audit-management', role: 'Provides audit-ready evidence trails for every traceable event' },
  { icon: GitBranch, name: 'End-to-End Traceability', slug: 'traceability', role: 'The unified genealogy layer connecting material, process, and dispatch' },
  { icon: Cpu, name: 'IoT & Machine Data', slug: 'iot-machine-data', role: 'Adds machine-linked events, sensor data, and process parameters to the trace chain' },
  { icon: BarChart3, name: 'Management Dashboards', slug: 'management-dashboards', role: 'Provides instant traceability visibility and search across the plant' },
];

const vendorConnectApps = [
  { icon: UserPlus, name: 'Supplier Onboarding', slug: 'supplier-onboarding', role: 'Qualifies suppliers with documentation and approval records' },
  { icon: FileSearch, name: 'Supplier Audits', slug: 'supplier-audits', role: 'Audit findings and scores linked to supplier performance history' },
  { icon: Award, name: 'Certificate Management', slug: 'certificate-management', role: 'Tracks supplier certificates, test reports, and expiry dates' },
  { icon: MessageSquareWarning, name: 'Supplier Complaints', slug: 'supplier-complaints', role: 'Links incoming quality rejections back to supplier for resolution' },
  { icon: CheckCircle, name: 'Corrective Actions', slug: 'corrective-actions', role: 'Tracks supplier CAPA with root cause and verified closure' },
  { icon: Star, name: 'Ratings & Scorecards', slug: 'ratings-scorecards', role: 'PPM, delivery, and audit scores inform supplier selection decisions' },
];

const industryExamples = [
  { industry: 'Automotive', color: RED, points: ['Part genealogy from raw material to dispatch', 'Supplier lot linkage with IATF-grade quality records', 'Process-stage traceability with operator and machine data', 'Recall readiness with instant backward trace'] },
  { industry: 'Pharmaceuticals', color: GREEN, points: ['Batch genealogy with GMP-grade documentation', 'Calibration and approval record linkage', 'Stage-wise inspection and deviation tracking', 'Complete audit trail for regulatory compliance'] },
  { industry: 'FMCG', color: AMBER, points: ['Batch and pack traceability across high-speed lines', 'Ingredient and raw material lot linkage', 'Recall readiness with forward trace capability', 'CCP monitoring linked to production batches'] },
  { industry: 'Aerospace', color: BLUE, points: ['Component genealogy with multi-level inspection records', 'Deviation and concession traceability', 'Approval chains linked to production stages', 'Full documentation trail for customer audit readiness'] },
  { industry: 'Export Manufacturing', color: '#A78BFA', points: ['Buyer compliance records linked to production data', 'Process and quality traceability for export documentation', 'Shipment documentation with complete batch history', 'CAPA and complaint linkage for buyer audits'] },
];

const iotCapabilities = [
  { icon: Cpu, title: 'PLC & Sensor Connectivity', desc: 'Direct machine data capture from PLCs, sensors, and controllers, feeding real-time production events into the traceability chain.' },
  { icon: BarChart3, title: 'Runtime, Downtime & OEE', desc: 'Machine-level runtime, downtime, and OEE data linked to production batches and operator shifts for complete machine genealogy.' },
  { icon: AlertTriangle, title: 'Andon & Process Alerts', desc: 'Real-time alerts for machine faults, process deviations, and quality events. Captured and linked to the affected production lot.' },
  { icon: ScanBarcode, title: 'Barcode & Sensor Capture', desc: 'Barcode-driven material movement, operator login, and process checkpoints create a structured digital event trail at every stage.' },
  { icon: Monitor, title: 'Live Line Dashboards', desc: 'Real-time line status, output tracking, and process visibility. Accessible from the shopfloor to the management office.' },
  { icon: Database, title: 'Machine-Linked Genealogy', desc: 'Every machine event is linked to the product, batch, operator, and shift, making machine data a core part of product traceability.' },
];

const platformViews = [
  { icon: Monitor, device: 'Desktop', view: 'Full genealogy search, multi-parameter trace, and management dashboards with complete plant visibility.' },
  { icon: Laptop, device: 'Laptop', view: 'Traceability reports, batch history, and CAPA linkage for quality managers and department heads.' },
  { icon: Tablet, device: 'Tablet', view: 'Barcode scanning, material issue and receipt, and inspection checksheets on the shopfloor.' },
  { icon: Smartphone, device: 'Mobile', view: 'Trace search, alerts, approval workflows, and instant batch lookup from anywhere.' },
];


/* ═══════════════════════════════════════════════════════
   ANIMATED PROCESS FLOW COMPONENT
   ═══════════════════════════════════════════════════════ */

function TraceFlowStep({ step, index, total }) {
  const [ref, inView] = useInView();
  const isLast = index === total - 1;

  return (
    <div ref={ref} className={`relative flex items-start gap-4 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: `${index * 150}ms` }}>
      {/* Vertical line + node */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center z-10 transition-all duration-500"
          style={{
            borderColor: inView ? step.color : 'rgba(255,255,255,0.06)',
            backgroundColor: inView ? `${step.color}15` : 'transparent',
          }}>
          <div className="w-2.5 h-2.5 rounded-full transition-all duration-700" style={{ backgroundColor: inView ? step.color : 'rgba(255,255,255,0.1)' }} />
        </div>
        {!isLast && (
          <div className="w-[2px] h-14 transition-all duration-1000" style={{ backgroundColor: inView ? `${step.color}40` : 'rgba(255,255,255,0.04)' }} />
        )}
      </div>

      {/* Content */}
      <div className="pt-1.5 pb-6">
        <h4 className="text-sm font-bold text-white/85 uppercase tracking-tight">{step.label}</h4>
        <p className="text-[12px] text-white/35 mt-1 font-medium leading-relaxed">{step.detail}</p>
      </div>
    </div>
  );
}

/* Side links for the process flow */
function SideLink({ label, color = RED }) {
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border" style={{ backgroundColor: `${color}08`, borderColor: `${color}15` }}>
      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: `${color}CC` }}>{label}</span>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════ */

export default function Manufacturing4Page() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 500);
    return () => clearTimeout(t);
  }, [setHeroVisible]);

  return (
    <div data-testid="manufacturing4-page">
      {/* ═══ 1. HERO ═══ */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#060606] overflow-hidden" data-testid="m4-hero">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(${GREEN}30 1px, transparent 1px), linear-gradient(90deg, ${GREEN}30 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }} />
        <div className="absolute top-20 right-20 w-[500px] h-[400px] rounded-full blur-[200px]" style={{ backgroundColor: `${GREEN}05` }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[300px] rounded-full blur-[180px]" style={{ backgroundColor: `${RED}04` }} />

        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 relative z-10">
          <div className={`transition-all duration-[1200ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '100ms' }}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: GREEN }} />
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: GREEN }}>Manufacturing 4.0</p>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-[4.5rem] font-black uppercase tracking-[-0.03em] text-white leading-[0.92] max-w-5xl" data-testid="m4-hero-title">
            <TypewriterLine text="End-to-End" visible={heroVisible} startDelay={400} highlightColor={GREEN} />
            <TypewriterLine text="Traceability for" visible={heroVisible} startDelay={1200} />
            <TypewriterLine text="Modern Manufacturing" visible={heroVisible} startDelay={2200} />
          </h1>

          <div className={`transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '4000ms' }}>
            <div className="mt-10 h-[3px] w-16" style={{ backgroundColor: GREEN }} />
            <p className="mt-8 text-lg text-white/45 max-w-2xl leading-[1.7] font-medium">
              <span className="text-white font-bold">MUSHIN</span> connects material, machines, operators, inspections, suppliers, and dispatch into one digital traceability chain. Trace every part. Every batch. Every event.
            </p>
          </div>

          <div className={`mt-10 flex flex-wrap gap-4 transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '4400ms' }}>
            <Link to="/contact" className="text-white px-8 py-3.5 rounded-full font-bold text-sm inline-flex items-center gap-2 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: RED }} data-testid="m4-hero-cta">
              Book a Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border-2 border-white/15 hover:border-white/30 text-white/50 hover:text-white px-8 py-3.5 rounded-full font-bold text-sm transition-all duration-300" data-testid="m4-hero-quote">
              Talk to Sales
            </a>
          </div>
        </div>

        <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 transition-all duration-1000 ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '5000ms' }}>
          <ArrowDown className="w-5 h-5 text-white/15 animate-bounce" />
        </div>
      </section>

      {/* ═══ 2. WHY TRACEABILITY IS BROKEN ═══ */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>The Reality</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Most Factories Capture Fragments of Traceability
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Records exist across paper registers, disconnected software, and isolated barcode systems. Very few factories connect the full chain from supplier to dispatch.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {brokenReasons.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 hover:border-[#E02D45]/15 transition-all duration-300" data-testid={`broken-reason-${i}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 border" style={{ backgroundColor: `${RED}08`, borderColor: `${RED}15` }}>
                    <Icon className="w-5 h-5" style={{ color: `${RED}CC` }} strokeWidth={1.7} />
                  </div>
                  <h3 className="text-sm font-bold text-white/80 uppercase">{item.title}</h3>
                  <p className="mt-2 text-[12px] text-white/35 leading-relaxed font-medium">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ═══ 3. WHAT TRUE TRACEABILITY MEANS ═══ */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: GREEN }}>True Traceability</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Every Material. Every Process. Every Record. Connected.
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              True end-to-end traceability means every material movement, every production event, every quality record, and every dispatch is digitally connected and instantly retrievable.
            </p>
          </div>

          {/* Traceability chain visual */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 sm:p-10" data-testid="trace-chain-visual">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/20 mb-10 text-center">The Complete Traceability Chain</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {traceStages.map((stage, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]" data-testid={`trace-stage-${i}`}>
                  <div className="flex flex-col items-center flex-shrink-0 mt-1">
                    <div className="w-7 h-7 rounded-full border flex items-center justify-center" style={{ borderColor: `${stage.color}40`, backgroundColor: `${stage.color}10` }}>
                      <span className="text-[9px] font-bold font-mono" style={{ color: stage.color }}>{String(i + 1).padStart(2, '0')}</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-[12px] font-bold text-white/80 uppercase">{stage.label}</h4>
                    <p className="text-[11px] text-white/30 mt-0.5 font-medium leading-relaxed">{stage.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 4. TRACEABILITY ARCHITECTURE — HOW EVERYTHING FITS ═══ */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-6">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>The Architecture</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Traceability Is Not One Module. It Is the Output of a Connected System.
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Mushin solves traceability by connecting multiple products and apps into one unified digital backbone, from supplier to dispatch.
            </p>
          </div>

          {/* System architecture diagram */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 sm:p-10 mt-10" data-testid="trace-architecture">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/20 mb-8 text-center">Mushin Traceability Ecosystem</p>

            {/* Flow: Supplier -> Incoming -> Stores -> Production -> Inspection -> Dispatch */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Supplier', sub: 'Vendor Connect', icon: Users, color: BLUE },
                { label: 'Incoming', sub: 'IQC / GRN', icon: ClipboardCheck, color: BLUE },
                { label: 'Stores', sub: 'Bins / FIFO / Lots', icon: Warehouse, color: GREEN },
                { label: 'Production', sub: 'Machine / Shift', icon: Factory, color: RED },
                { label: 'Inspection', sub: 'Quality / SPC', icon: ShieldCheck, color: RED },
                { label: 'Dispatch', sub: 'Customer / Ship', icon: Truck, color: GREEN },
              ].map((node, i) => {
                const Icon = node.icon;
                return (
                  <div key={i} className="relative">
                    <div className="flex flex-col items-center text-center p-4 rounded-xl border bg-white/[0.02]" style={{ borderColor: `${node.color}15` }}>
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 border" style={{ backgroundColor: `${node.color}10`, borderColor: `${node.color}20` }}>
                        <Icon className="w-5 h-5" style={{ color: node.color }} strokeWidth={1.7} />
                      </div>
                      <p className="text-[11px] font-bold text-white/80 uppercase">{node.label}</p>
                      <p className="text-[9px] text-white/25 mt-0.5 font-medium">{node.sub}</p>
                    </div>
                    {i < 5 && (
                      <div className="hidden lg:flex absolute top-1/2 -right-2 -translate-y-1/2 z-10">
                        <ChevronRight className="w-3.5 h-3.5 text-white/10" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Supporting systems strip */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <SideLink label="CAPA" color={RED} />
              <SideLink label="Complaints" color={RED} />
              <SideLink label="Audits" color={AMBER} />
              <SideLink label="4M Changes" color={AMBER} />
              <SideLink label="Certificates" color={BLUE} />
              <SideLink label="Machine Events" color={GREEN} />
              <SideLink label="Barcode Scans" color={GREEN} />
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 5. WHICH APPS MAKE IT WORK ═══ */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>The Apps That Build the Chain</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Two Products. Modular Apps. One Traceability Chain.
            </h2>
          </div>

          {/* Factory OS Apps */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: RED }} />
              <h3 className="text-base font-bold text-white/60 uppercase tracking-[0.1em]">Mushin Factory OS</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {factoryOSApps.map((app, i) => {
                const Icon = app.icon;
                return (
                  <Link key={i} to={`/products/factory-os#${app.slug}`} className="group bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#E02D45]/20 block" data-testid={`fos-app-${app.slug}`}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                      <Icon className="w-[18px] h-[18px]" style={{ color: RED }} strokeWidth={1.7} />
                    </div>
                    <h4 className="text-[12px] font-bold text-white/80 leading-snug">{app.name}</h4>
                    <p className="mt-1 text-[11px] text-white/30 leading-relaxed font-medium">{app.role}</p>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Vendor Connect Apps */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BLUE }} />
              <h3 className="text-base font-bold text-white/60 uppercase tracking-[0.1em]">Mushin Vendor Connect</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
              {vendorConnectApps.map((app, i) => {
                const Icon = app.icon;
                return (
                  <Link key={i} to={`/products/vendor-connect#${app.slug}`} className="group bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#38BDF8]/20 block" data-testid={`vc-app-${app.slug}`}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 border" style={{ backgroundColor: `${BLUE}10`, borderColor: `${BLUE}20` }}>
                      <Icon className="w-[18px] h-[18px]" style={{ color: BLUE }} strokeWidth={1.7} />
                    </div>
                    <h4 className="text-[12px] font-bold text-white/80 leading-snug">{app.name}</h4>
                    <p className="mt-1 text-[11px] text-white/30 leading-relaxed font-medium">{app.role}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 6. ANIMATED PROCESS FLOW ═══ */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left: Heading + context */}
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: GREEN }}>The Traceability Journey</p>
              <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
                Data Flows Through Every Stage of Manufacturing
              </h2>
              <p className="mt-5 text-base text-white/40 max-w-md leading-[1.7] font-medium">
                From the moment raw material enters your factory to the moment a finished product is dispatched, every event is captured, linked, and retrievable.
              </p>

              <div className="mt-8 space-y-3">
                <p className="text-[12px] text-white/25 font-bold uppercase tracking-[0.2em] mb-4">Supporting Systems at Every Stage</p>
                <div className="flex flex-wrap gap-2">
                  <SideLink label="CAPA" color={RED} />
                  <SideLink label="Complaints" color={RED} />
                  <SideLink label="Change Management" color={AMBER} />
                  <SideLink label="Audit Evidence" color={AMBER} />
                  <SideLink label="Supplier Certificates" color={BLUE} />
                  <SideLink label="Machine Alarms" color={GREEN} />
                  <SideLink label="OEE Events" color={GREEN} />
                  <SideLink label="Barcode Scans" color={GREEN} />
                </div>
              </div>
            </div>

            {/* Right: Animated flow */}
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 sm:p-8" data-testid="animated-trace-flow">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/20 mb-6 text-center">Traceability Process Flow</p>
              <div>
                {traceStages.map((step, i) => (
                  <TraceFlowStep key={i} step={step} index={i} total={traceStages.length} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 7. FORWARD TRACE / BACKWARD TRACE ═══ */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14 text-center mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: AMBER }}>Bidirectional Trace</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Trace Forward. Trace Backward. Instantly.
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl mx-auto leading-[1.7] font-medium">
              Whether you are tracking where a raw material lot ended up, or tracing a customer complaint back to its origin, Mushin gives you both directions instantly.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="trace-directions">
            {/* Forward Trace */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-7 hover:border-[#34D399]/15 transition-all duration-300">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center border" style={{ backgroundColor: `${GREEN}10`, borderColor: `${GREEN}20` }}>
                  <ArrowRight className="w-5 h-5" style={{ color: GREEN }} strokeWidth={1.7} />
                </div>
                <h3 className="text-lg font-bold text-white/85 uppercase tracking-tight">Forward Trace</h3>
              </div>
              <p className="text-[13px] text-white/40 leading-relaxed font-medium mb-5">
                Start from a raw material lot, supplier, or incoming batch and trace exactly where it went through stores, production, inspection, and dispatch.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Supplier Lot', 'GRN', 'Bin / Store', 'Production Batch', 'Inspection', 'Dispatch'].map((s, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/50 bg-white/[0.04] px-2.5 py-1 rounded">{s}</span>
                    {i < 5 && <ChevronRight className="w-3 h-3 text-white/10" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Backward Trace */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-7 hover:border-[#F59E0B]/15 transition-all duration-300">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center border" style={{ backgroundColor: `${AMBER}10`, borderColor: `${AMBER}20` }}>
                  <ArrowLeftRight className="w-5 h-5" style={{ color: AMBER }} strokeWidth={1.7} />
                </div>
                <h3 className="text-lg font-bold text-white/85 uppercase tracking-tight">Backward Trace</h3>
              </div>
              <p className="text-[13px] text-white/40 leading-relaxed font-medium mb-5">
                Start from a finished product, customer complaint, dispatch batch, or audit finding and trace backward to the exact material, supplier, process, and operator.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Complaint', 'Dispatch', 'Inspection', 'Production', 'Store Issue', 'Supplier'].map((s, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/50 bg-white/[0.04] px-2.5 py-1 rounded">{s}</span>
                    {i < 5 && <ChevronRight className="w-3 h-3 text-white/10" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 8. INDUSTRY-SPECIFIC EXAMPLES ═══ */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Industry Traceability</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              One Backbone. Every Industry.
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              The same traceability architecture adapts to the specific requirements of your industry, from automotive part genealogy to pharma batch compliance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:grid-rows-2">
            {industryExamples.map((ex, i) => (
              <div key={i} className={`bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 hover:border-opacity-30 transition-all duration-300 ${i === 0 ? 'lg:row-span-2' : ''}`} style={{ '--hover-color': ex.color }} data-testid={`industry-trace-${ex.industry.toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ex.color }} />
                  <h3 className="text-sm font-bold text-white/80 uppercase tracking-tight">{ex.industry}</h3>
                </div>
                <div className="space-y-2.5">
                  {ex.points.map((point, j) => (
                    <div key={j} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: ex.color }} strokeWidth={2.5} />
                      <span className="text-[12px] text-white/40 font-medium leading-relaxed">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ═══ 9. INDUSTRY 4.0 / IoT ═══ */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: GREEN }}>Industry 4.0 Execution</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              IoT and Machine Data Strengthen the Traceability Layer
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Industry 4.0 is not just about dashboards. It is about connecting machine events, sensor data, and process signals directly into your product traceability chain.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {iotCapabilities.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 hover:border-[#34D399]/15 transition-all duration-300" data-testid={`iot-capability-${i}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 border" style={{ backgroundColor: `${GREEN}10`, borderColor: `${GREEN}20` }}>
                    <Icon className="w-5 h-5" style={{ color: GREEN }} strokeWidth={1.7} />
                  </div>
                  <h3 className="text-sm font-bold text-white/80 uppercase">{item.title}</h3>
                  <p className="mt-2 text-[12px] text-white/35 leading-relaxed font-medium">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ═══ 10. CROSS-PLATFORM VISIBILITY ═══ */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14 text-center mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: BLUE }}>Cross-Platform Access</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Traceability Visibility Across Every Device
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl mx-auto leading-[1.7] font-medium">
              Access genealogy data, trace search, barcode workflows, and management dashboards from desktop, laptop, tablet, or mobile. Wherever your team works.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {platformViews.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 text-center hover:border-[#38BDF8]/15 transition-all duration-300" data-testid={`platform-${p.device.toLowerCase()}`}>
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 border" style={{ backgroundColor: `${BLUE}10`, borderColor: `${BLUE}20` }}>
                    <Icon className="w-6 h-6" style={{ color: BLUE }} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sm font-bold text-white/80 uppercase mb-2">{p.device}</h3>
                  <p className="text-[12px] text-white/35 leading-relaxed font-medium">{p.view}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ═══ 11. FINAL CTA ═══ */}
      <section className="py-28 lg:py-40 relative overflow-hidden bg-[#060606] border-t border-white/[0.04]" data-testid="m4-cta-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[150px]" style={{ backgroundColor: `${GREEN}06` }} />
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 text-center relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8" style={{ color: GREEN }}>Get Started</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1]">
            Build True End-to-End<br />Traceability with Mushin
          </h2>
          <p className="mt-6 text-white/35 text-lg max-w-lg mx-auto font-medium leading-[1.7]">
            Trace every material. Connect every process. Retrieve any batch, part, or shipment instantly. Run it on <span className="text-white font-bold">MUSHIN</span>.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="text-white px-10 py-4 rounded-full font-bold text-base inline-flex items-center gap-2.5 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: RED }} data-testid="m4-cta-demo">
              Book a Demo <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border-2 border-white/15 hover:border-white/30 text-white/45 hover:text-white px-10 py-4 rounded-full font-bold text-base transition-all duration-300" data-testid="m4-cta-sales">
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      <BookDemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
